import {
    CurrentUserInfo,
    PaymentBarData,
    PaymentBarNotificationPayload,
    SocketConfig
} from '../types'
import { logger } from '../utils/logger'
import { authenticatedFetch } from './auth'

type RemoteSocketConfigResponse = Partial<SocketConfig> | { config?: Partial<SocketConfig> | null } | null

const CURRENT_USER_ENDPOINT = '/api/mobile-shop/currentUser'
const SOCKET_CONFIG_ENDPOINT = '/api/mobile-shop/sockjsConfig'
const DEFAULT_CHANNEL_PREFIX = 'mobile-shop'
const DEFAULT_SOCKET_ENDPOINT = 'socket.io'

// Cache for current user info to avoid duplicate API calls
let userInfoGeneration = 0
let cachedUserInfo: CurrentUserInfo | null = null
let userInfoPromise: Promise<CurrentUserInfo> | null = null

const normalizeProtocol = (protocol?: string): 'http' | 'https' => {
    if (!protocol) {
        return 'https'
    }

    const lowered = protocol.toLowerCase()

    if (lowered === 'http' || lowered === 'https') {
        return lowered as 'http' | 'https'
    }

    if (lowered === 'ws' || lowered === 'wss') {
        return lowered === 'ws' ? 'http' : 'https'
    }

    return 'https'
}

const normalizeEndpoint = (endpoint?: string): string => {
    if (!endpoint) {
        return DEFAULT_SOCKET_ENDPOINT
    }

    return endpoint.replace(/^\/+/, '')
}

const buildDefaultConfig = (): SocketConfig => {
    if (typeof window === 'undefined') {
        return {
            protocol: 'https',
            host: 'localhost',
            port: 443,
            endpoint: DEFAULT_SOCKET_ENDPOINT
        }
    }

    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    const port = window.location.port ? Number(window.location.port) : protocol === 'https' ? 443 : 80

    return {
        protocol,
        host: window.location.hostname,
        port,
        endpoint: DEFAULT_SOCKET_ENDPOINT
    }
}

const extractBaseConfig = (raw: RemoteSocketConfigResponse): Partial<SocketConfig> | null => {
    if (!raw) {
        return null
    }

    if (typeof raw === 'object' && raw !== null && 'config' in raw) {
        const nested = (raw as { config?: Partial<SocketConfig> | null }).config
        return nested ?? null
    }

    return raw as Partial<SocketConfig>
}

export const normalizeSocketConfig = (raw: RemoteSocketConfigResponse): SocketConfig => {
    const baseConfig = extractBaseConfig(raw)

    if (!baseConfig) {
        return buildDefaultConfig()
    }

    const protocol = normalizeProtocol(baseConfig.protocol)
    const defaultPort = protocol === 'https' ? 443 : 80

    return {
        protocol,
        host: baseConfig.host ?? (typeof window !== 'undefined' ? window.location.hostname : 'localhost'),
        port: baseConfig.port ?? defaultPort,
        endpoint: normalizeEndpoint(baseConfig.endpoint),
        msgpack: baseConfig.msgpack,
        reconnectionDelay: baseConfig.reconnectionDelay,
        enableLogging: baseConfig.enableLogging
    }
}

/**
 * Fetch current user info from API
 * Uses caching to avoid duplicate calls
 */
const fetchCurrentUserInfo = async (): Promise<CurrentUserInfo> => {
    if (userInfoPromise) return userInfoPromise
    if (cachedUserInfo) return cachedUserInfo
    const generation = userInfoGeneration
    userInfoPromise = (async () => {
        try {
            const response = await authenticatedFetch(CURRENT_USER_ENDPOINT, { cache: 'no-store' })
            if (!response.ok) throw new Error('Failed to load current user info')
            const json = await response.json()
            const data = json.data as CurrentUserInfo | undefined
            if (json.r !== true || !data || !['string', 'number'].includes(typeof data.id)) {
                throw new Error('Invalid current user response')
            }
            if (generation !== userInfoGeneration) throw new Error('Session changed')
            cachedUserInfo = data
            return data
        } finally {
            if (generation === userInfoGeneration) userInfoPromise = null
        }
    })()
    return userInfoPromise
}

export const getCurrentUserInfo = async (options?: { refresh?: boolean }): Promise<CurrentUserInfo> => {
    if (options?.refresh) cachedUserInfo = null
    return fetchCurrentUserInfo()
}

/**
 * Clear the cached user info (useful when logging out or auth state changes)
 */
export const clearCachedUserInfo = (): void => {
    userInfoGeneration += 1
    cachedUserInfo = null
    userInfoPromise = null
    logger.log('API', 'Cleared cached user info')
}

/**
 * Check if the user is currently authenticated
 * Uses the same cached user info to avoid duplicate API calls
 * @returns true if authenticated, false otherwise
 */
export const checkAuthentication = async (): Promise<boolean> => {
    try {
        // Use the cached fetch function which handles caching and deduplication
        const userInfo = await fetchCurrentUserInfo()

        // Check if we have valid user data (and it's not the fallback)
        const isAuthenticated = !!(userInfo && userInfo.id && userInfo.id !== '')
        
        logger.log('AUTH', 'Authentication check result', {
            isAuthenticated,
            userId: userInfo?.id,
        })

        return isAuthenticated
    } catch (error) {
        logger.warn('AUTH', 'Authentication check failed:', error)
        return false
    }
}

export const getSocketConfig = async (): Promise<SocketConfig> => {
    try {
        const response = await authenticatedFetch(SOCKET_CONFIG_ENDPOINT)

        if (!response.ok) {
            throw new Error('Failed to load socket config')
        }

        const json = await response.json()
        const data = (json as { r: boolean; data: { config?: Partial<SocketConfig> | null } }).data
        const configData: RemoteSocketConfigResponse = data.config ? { config: data.config } : null
        
        return normalizeSocketConfig(configData)
    } catch (error) {
        logger.warn('API', 'Falling back to default socket config:', error)
        return buildDefaultConfig()
    }
}

export const resolveChannel = (_config: SocketConfig, user?: CurrentUserInfo): string | undefined => {
    if (!user) {
        logger.warn('SOCKET', 'No user provided for channel resolution')
        return undefined
    }

    const identifier = user.walletId ?? user.id

    if (identifier === undefined || identifier === null) {
        logger.warn('SOCKET', 'No identifier found in user', {
            userId: user.id,
            walletId: user.walletId,
        })
        return undefined
    }

    const channel = `${DEFAULT_CHANNEL_PREFIX}:${identifier}`
    logger.log('SOCKET', 'Channel resolved', { channel, identifier })
    return channel
}

export const parsePaymentBarMessage = (raw: unknown): PaymentBarData | null => {
    logger.log('PAYMENT', '🔍 parsePaymentBarMessage called', {
        raw,
        rawType: typeof raw,
        isObject: typeof raw === 'object' && raw !== null
    })
    
    if (raw === null || raw === undefined) {
        logger.log('PAYMENT', '❌ Raw is null/undefined')
        return null
    }

    let payload: PaymentBarNotificationPayload | PaymentBarData | undefined

    try {
        payload =
            typeof raw === 'string'
                ? (JSON.parse(raw) as PaymentBarNotificationPayload | PaymentBarData)
                : (raw as PaymentBarNotificationPayload | PaymentBarData)
        
        logger.log('PAYMENT', '✅ Parsed payload', {
            payload,
            hasMobilePaymentBar: 'mobilePaymentBar' in (payload as Record<string, unknown>),
            hasAmount: 'amount' in (payload as Record<string, unknown>),
            hasConfig: 'config' in (payload as Record<string, unknown>),
            hasRewards: 'rewards' in (payload as Record<string, unknown>)
        })
    } catch (error) {
        logger.error('PAYMENT', '❌ Unable to parse message payload', error)
        return null
    }

    if (!payload || typeof payload !== 'object') {
        logger.log('PAYMENT', '❌ Payload is not an object')
        return null
    }

    // Check if payload is already PaymentBarData (direct format from socket)
    if ('amount' in payload && 'config' in payload) {
        logger.log('PAYMENT', '✅ Payload is direct PaymentBarData format', payload)
        return payload as PaymentBarData
    }

    // Check for wrapped formats
    const wrapped = payload as PaymentBarNotificationPayload
    const result = 
        wrapped.mobilePaymentBar ??
        wrapped.mobileBonusBar ??
        wrapped.paymentBar ??
        wrapped.data?.mobilePaymentBar ??
        null
    
    logger.log('PAYMENT', '🔍 Checked wrapped formats', {
        hasMobilePaymentBar: !!wrapped.mobilePaymentBar,
        hasMobileBonusBar: !!wrapped.mobileBonusBar,
        hasPaymentBar: !!wrapped.paymentBar,
        hasDataMobilePaymentBar: !!wrapped.data?.mobilePaymentBar,
        result: result ? 'found' : 'null'
    })
    
    return result
}

